/* eslint-disable */
import { ITechnicalReport } from '../types';
import { CandidateType } from '../types/candidate-type';
import config from '../utils/config';
import ResourceManager from './resource.manager';

class JobService extends ResourceManager {
	getCompanies(detailed = false) {
		return this.getResource(detailed ? config.URIs.companies_uri : config.URIs.companies_summary_uri);
	}

	getCompany(companyId): Promise<any> {
		return this.getResource(config.URIs.get_company_uri.replace(/:companyId/g, companyId));
	}

	getCurrentCompany(): Promise<any> {
		return this.getResource(config.URIs.get_current_company);
	}

	getJob(jobId): Promise<any> {
		return this.getResource(config.URIs.get_job_uri.replace(/:jobId/g, jobId));
	}

	getJobs(params?, options?, detailed?): Promise<any> {
		return this.getResource(detailed ? config.URIs.jobs : config.URIs.jobs_summary, {
			...(options || {}),
			params
		});
	}

	postJob(data): Promise<any> {
		return this.postResource(config.URIs.post_job, data);
	}

	cloneJob(jobId, options?): Promise<any> {
		return this.postResource(config.URIs.clone_job.replace(/:jobId/g, jobId), options);
	}

	editJob(jobId, data): Promise<any> {
		return this.patchResource(config.URIs.edit_job.replace(/:jobId/g, jobId), data);
	}

	getJobApplications(jobId, state?): Promise<any> {
		const params: any = {};
		if (state) {
			params.state = state;
		}
		return this.getResource(config.URIs.get_job_applications.replace(/:jobId/g, jobId), { params });
	}

	getInterviewAssignableCandidates(jobId): Promise<any> {
		return this.getJobApplications(jobId, 'toInterview').then(apps => apps.map(app => app.candidate));
	}

	recommendCandidate(jobId, candidate): Promise<any> {
		return this.postResource(config.URIs.recommend_candidate.replace(/:jobId/g, jobId), candidate);
	}

	async editJobApplication(appId, application): Promise<any> {
		return this.putResource(config.URIs.job_application_edit.replace(':applicationId', appId), application);
	}

	async unLockJobApplication(appId, application): Promise<any> {
		return this.putResource(config.URIs.job_application_unlock.replace(':applicationId', appId), application);
	}

	async attachReportToApplication(jobId: string, applicationId: string, report: ITechnicalReport): Promise<any> {
		return await this.patchResource(
			config.URIs.attach_report_to_application
				.replace(/:jobId/g, jobId)
				.replace(/:applicationId/g, applicationId),
			report
		);
	}

	async assignCandidateToInterview(jobId, slotId, candidatesId, force = false, candidateType): Promise<any> {
		return await this.patchResource(
			config.URIs.assign_candidate_to_interview.replace(/:jobId/g, jobId).replace(/:slotId/g, slotId) +
				(force ? `?force=true` : ''),
			{
				candidate: candidatesId
			},
			{
				params: { source: candidateType ?? CandidateType.TALENTS_ARENA }
			}
		);
	}

	async cancelSlot(slotId): Promise<any> {
		return await this.patchResource(config.URIs.cancel_slot_uri.replace(/:slotId/g, slotId), undefined);
	}

	async changeApplicationState(
		applicationId,
		{ fromState, toState, position = undefined, currentStep, stepsStatus, reason = undefined }
	): Promise<any> {
		return await this.patchResource(
			config.URIs.change_application_state.replace(/:applicationId/g, applicationId),
			{
				fromState,
				toState,
				currentStep,
				stepsStatus,
				reason,
				position
			}
		);
	}

	async getInterviewSlots() {
		const slots: any = await this.getResource(config.URIs.get_interview_slots);
		return slots.map(slot => ({
			...slot,
			start: new Date(slot.start),
			end: new Date(slot.end)
		}));
	}

	async getInterviewSlotsOfJob(jobId) {
		const slots: any = await this.getResource(config.URIs.get_job_interview_slots.replace(/:jobId/g, jobId));
		return slots.map(slot => ({
			...slot,
			start: new Date(slot.start),
			end: new Date(slot.end)
		}));
	}

	async addInterviewSlot(jobId, interviewSlotData) {
		const res: any = await this.postResource(
			config.URIs.post_interview_slot_uri.replace(/:id/g, jobId),
			interviewSlotData
		);
		if (res) {
			res.start = new Date(res.start);
			res.end = new Date(res.end);
			return res;
		}
		return res;
	}

	async editInterviewSlot(slotId, interviewSlotData) {
		const res = await this.putResource(
			config.URIs.edit_interview_slot_uri.replace(/:slotId/g, slotId),
			interviewSlotData
		);
		return res;
	}

	makeOffer(jobId, applicationId, offer) {
		return this.postResource(
			config.URIs.make_offer.replace(/:jobId/g, jobId).replace(/:applicationId/g, applicationId),
			offer
		);
	}

	counterOffer(jobId, offerId, offer) {
		return this.postResource(
			config.URIs.counter_offer.replace(/:jobId/g, jobId).replace(/:offerId/g, offerId),
			offer
		);
	}

	withdrawOffer(jobId, offerId) {
		return this.patchResource(
			config.URIs.withdraw_offer.replace(/:jobId/g, jobId).replace(/:offerId/g, offerId),
			undefined
		);
	}

	rejectOffer(jobId, offerId) {
		return this.patchResource(
			config.URIs.reject_offer.replace(/:jobId/g, jobId).replace(/:offerId/g, offerId),
			undefined
		);
	}

	acceptOffer(jobId, offerId) {
		return this.patchResource(
			config.URIs.accept_offer.replace(/:jobId/g, jobId).replace(/:offerId/g, offerId),
			undefined
		);
	}

	updateOfferCounter(jobId, offerId, offer) {
		return this.patchResource(`${config.URIs.update_offer_counter}/${jobId}/${offerId}`, offer);
	}

	async getOffers(jobId?, params?, options?) {
		let offers;
		if (jobId) {
			({ offers } = await this.getResource<any>(config.URIs.get_job_offers.replace(/:jobId/g, jobId)));
		} else {
			offers = await this.getResource(config.URIs.get_offers, {
				...(options || {}),
				params
			});
		}

		return offers;
	}

	async addInterviewsQuota(companyId, permissions) {
		return await this.putResource(config.URIs.edit_permitted_interviews.replace(/:companyId/g, companyId), {
			permissions
		});
	}

	async editSpentInterviews(companyId, spentInterviews) {
		return await this.putResource(config.URIs.edit_spent_interviews.replace(/:companyId/g, companyId), {
			spentInterviews
		});
	}

	async removeApplicationLabel(jobId, applicationId, labelId) {
		return await this.deleteResource(
			config.URIs.remove_applicant_label
				.replace(/:jobId/g, jobId)
				.replace(/:applicationId/g, applicationId)
				.replace(/:labelId/g, labelId)
		);
	}

	async toggle_offer(offerId) {
		return await this.getResource(`${config.URIs.toggle_offer}/${offerId}`);
	}

	async sendRejectionMessage(app) {
		return await this.postResource(config.URIs.send_rejection_message, app);
	}

	async addApplicationLabel(jobId, applicationId, label) {
		return await this.postResource(
			config.URIs.add_applicant_label.replace(/:jobId/g, jobId).replace(/:applicationId/g, applicationId),
			label
		);
	}

	async setStatus(jobId, status) {
		return await this.putResource(config.URIs.set_job_status.replace(/:jobId/g, jobId), { status });
	}

	async removeApplicant(applicantId) {
		return await this.deleteResource(config.URIs.remove_application_from_job.replace(/:applicantId/g, applicantId));
	}

	async getApplicantComments(jobId, applicationId) {
		const resp: any = await this.getResource(
			config.URIs.get_applicant_comments.replace(/:jobId/g, jobId).replace(/:applicationId/g, applicationId)
		);
		return resp.map(comment => ({ ...comment, createdAt: new Date(comment.createdAt) }));
	}

	async commentOnApplicantCard(jobId, applicationId, comment, attachment) {
		var formData = new FormData(); // Currently empty
		formData.append('body', comment);
		formData.append('attachment', attachment);
		const resp: any = await this.postResource(
			config.URIs.comment_on_applicant.replace(/:jobId/g, jobId).replace(/:applicationId/g, applicationId),
			formData
		);

		return {
			...resp,
			createdAt: new Date(resp.createdAt)
		};
	}

	async uploadFile(file) {
		var formData = new FormData(); // Currently empty
		formData.append('file', file);
		const resp: any = await this.postResource(config.URIs.upload_attachments, formData);

		return {
			...resp,
			createdAt: new Date(resp.createdAt)
		};
	}

	async deleteApplicantComment(jobId, applicationId, commentId) {
		try {
			await this.deleteResource(
				config.URIs.delete_applicant_comment
					.replace(/:jobId/g, jobId)
					.replace(/:applicationId/g, applicationId)
					.replace(/:commentId/g, commentId)
			);
			return true;
		} catch {
			return false;
		}
	}

	async editApplicantComment(jobId, applicationId, commentId, comment) {
		try {
			await this.putResource(
				config.URIs.edit_applicant_comment
					.replace(/:jobId/g, jobId)
					.replace(/:applicationId/g, applicationId)
					.replace(/:commentId/g, commentId),
				{
					body: comment
				}
			);
			return true;
		} catch {
			return false;
		}
	}

	async setEmoji(commentId, emoji) {
		try {
			await this.putResource(`${config.URIs.set_emoji_comment}/${commentId}`, {
				body: emoji
			});
			return true;
		} catch {
			return false;
		}
	}

	getWatchedJobsSummary(params?: any): Promise<any> {
		return this.getResource(config.URIs.jobs_watched_summary, { params });
	}

	getUserAccessJobs(userId: string): Promise<any> {
		return this.getResource(config.URIs.user_access_jobs.replace(/:userId/g, userId));
	}

	setUserAccessJobs(userId: string, jobIds: string[]): Promise<any> {
		return this.postResource(config.URIs.user_access_jobs.replace(/:userId/g, userId), jobIds);
	}

	async watchJob(jobId: string): Promise<boolean> {
		try {
			await this.patchResource<undefined, any>(config.URIs.watch_job.replace(/:jobId/g, jobId), undefined);
			return true;
		} catch {
			return false;
		}
	}

	async unwatchJob(jobId: string): Promise<boolean> {
		try {
			await this.deleteResource(config.URIs.unwatch_job.replace(/:jobId/g, jobId));
			return true;
		} catch {
			return false;
		}
	}

	async jobCVViewable(jobId: string, cvViewable: boolean): Promise<boolean> {
		try {
			const { success, data } = await this.patchResource<any, any>(
				config.URIs.job_cv_viewable.replace(/:jobId/g, jobId),
				{
					cvViewable
				}
			);
			if (!success) throw new Error('Something went wrong');
			return data;
		} catch {
			return false;
		}
	}
	async jobCVDownloadable(jobId: string, cvDownloadable: boolean): Promise<boolean> {
		try {
			const { success, data } = await this.patchResource<any, any>(
				config.URIs.job_cv_downloadable.replace(/:jobId/g, jobId),
				{ cvDownloadable }
			);
			if (!success) throw new Error('Something went wrong');
			return data;
		} catch {
			return false;
		}
	}
}

export default new JobService();
